import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Stack,
  Card,
  useTheme,
  alpha,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Block as BlockIcon,
  CheckCircle as ActivateIcon,
  VpnKey as ResetPasswordIcon,
  School as AlumniIcon,
} from '@mui/icons-material';
import { DataGrid, GridColDef, GridToolbar } from '@mui/x-data-grid';
import axios from 'axios';
import { useSettings } from '../../../contexts/SettingsContext';

const API_URL = import.meta.env.VITE_API_URL || 'https://stardust-aims.amy.care';

interface AlumniUser {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  role: string;
  is_active: boolean;
  is_verified: boolean;
  phone?: string;
  line_user_id?: string;
  created_at: string;
  last_login?: string;
  // Alumni information
  student_id?: string;
  citizen_id?: string;
  alumni_id?: string;
}

function AlumniUserManagement() {
  const theme = useTheme();
  const { formatDateTime } = useSettings();
  const [users, setUsers] = useState<AlumniUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<AlumniUser | null>(null);
  const [formData, setFormData] = useState({
    email: '',
    first_name: '',
    last_name: '',
    phone: '',
    line_user_id: '',
    password: '',
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const response = await axios.get(`${API_URL}/api/v1/auth/users`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { role: 'alumni' },
      });

      // Fetch Alumni data for each user to get student_id and citizen_id
      const usersWithAlumniData = await Promise.all(
        response.data.map(async (user: any) => {
          try {
            // Find alumni record by user_id
            const alumniResponse = await axios.get(
              `${API_URL}/api/v1/alumni/by-user/${user.id}`,
              { headers: { Authorization: `Bearer ${token}` } }
            );
            
            return {
              ...user,
              // Use alumni name instead of LINE display name
              first_name: alumniResponse.data.first_name_th || user.first_name,
              last_name: alumniResponse.data.last_name_th || user.last_name,
              student_id: alumniResponse.data.student_id,
              citizen_id: alumniResponse.data.citizen_id,
              alumni_id: alumniResponse.data.id,
            };
          } catch (alumniErr) {
            // If no alumni record found, return user without alumni data
            return {
              ...user,
              student_id: null,
              citizen_id: null,
              alumni_id: null,
            };
          }
        })
      );

      setUsers(usersWithAlumniData);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to fetch alumni users');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setSelectedUser(null);
    setFormData({
      email: '',
      first_name: '',
      last_name: '',
      phone: '',
      line_user_id: '',
      password: '',
    });
    setEditDialogOpen(true);
  };

  const handleEdit = (user: AlumniUser) => {
    setSelectedUser(user);
    setFormData({
      email: user.email,
      first_name: user.first_name,
      last_name: user.last_name,
      phone: user.phone || '',
      line_user_id: user.line_user_id || '',
      password: '',
    });
    setEditDialogOpen(true);
  };

  const handleDelete = (user: AlumniUser) => {
    setSelectedUser(user);
    setDeleteDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      const token = localStorage.getItem('token');
      const data: any = {
        ...formData,
        role: 'alumni',
      };

      if (!formData.password) {
        delete data.password;
      }

      if (!formData.line_user_id) {
        delete data.line_user_id;
      }

      if (selectedUser) {
        await axios.put(`${API_URL}/api/v1/auth/users/${selectedUser.id}`, data, {
          headers: { Authorization: `Bearer ${token}` },
        });
      } else {
        await axios.post(`${API_URL}/api/v1/auth/register`, data, {
          headers: { Authorization: `Bearer ${token}` },
        });
      }

      setEditDialogOpen(false);
      fetchUsers();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to save alumni user');
    }
  };

  const handleConfirmDelete = async () => {
    if (!selectedUser) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/v1/auth/users/${selectedUser.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setDeleteDialogOpen(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to delete alumni user');
    }
  };

  const handleToggleActive = async (user: AlumniUser) => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(
        `${API_URL}/api/v1/auth/users/${user.id}/toggle-active`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchUsers();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to toggle alumni user status');
    }
  };

  const handleResetPassword = async (user: AlumniUser) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/v1/auth/users/${user.id}/reset-password`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      alert(`New password for ${user.email}: ${response.data.new_password}\n\nPlease save this password and share it securely with the alumni.`);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to reset password');
    }
  };

  const filteredUsers = statusFilter === 'all' 
    ? users 
    : users.filter(u => u.is_active === (statusFilter === 'active'));

  const columns: GridColDef[] = [
    {
      field: 'email',
      headerName: 'Email',
      width: 250,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AlumniIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
          <Typography variant="body2" fontWeight={500}>
            {params.value}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'name',
      headerName: 'Name',
      width: 200,
      renderCell: (params) => `${params.row.first_name || ''} ${params.row.last_name || ''}`.trim() || '-',
    },
    {
      field: 'ids',
      headerName: 'Student ID / Citizen ID',
      width: 200,
      renderCell: (params) => (
        <Box>
          <Typography 
            variant="body2" 
            fontFamily="monospace"
            color={params.row.student_id ? 'text.primary' : 'text.disabled'}
          >
            {params.row.student_id || '-'}
          </Typography>
          <Typography 
            variant="caption" 
            fontFamily="monospace"
            color={params.row.citizen_id ? 'text.secondary' : 'text.disabled'}
          >
            {params.row.citizen_id || '-'}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'phone',
      headerName: 'Phone',
      width: 150,
    },
    {
      field: 'line_user_id',
      headerName: 'LINE ID',
      width: 150,
      renderCell: (params) => params.value || '-',
    },
    {
      field: 'is_active',
      headerName: 'Status',
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.value ? 'Active' : 'Inactive'}
          color={params.value ? 'success' : 'default'}
          size="small"
        />
      ),
    },
    {
      field: 'is_verified',
      headerName: 'Verified',
      width: 100,
      renderCell: (params) => (
        <Chip
          label={params.value ? 'Yes' : 'No'}
          color={params.value ? 'info' : 'default'}
          size="small"
          variant="outlined"
        />
      ),
    },
    {
      field: 'created_at',
      headerName: 'Created At',
      width: 180,
      renderCell: (params) => formatDateTime(params.value),
    },
    {
      field: 'last_login',
      headerName: 'Last Login',
      width: 180,
      renderCell: (params) => params.value ? formatDateTime(params.value) : '-',
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 200,
      sortable: false,
      renderCell: (params) => (
        <Stack direction="row" spacing={0.5}>
          <IconButton
            size="small"
            onClick={() => handleEdit(params.row)}
            sx={{ color: theme.palette.primary.main }}
          >
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => handleToggleActive(params.row)}
            sx={{ color: params.row.is_active ? theme.palette.warning.main : theme.palette.success.main }}
          >
            {params.row.is_active ? <BlockIcon fontSize="small" /> : <ActivateIcon fontSize="small" />}
          </IconButton>
          <IconButton
            size="small"
            onClick={() => handleResetPassword(params.row)}
            sx={{ color: theme.palette.info.main }}
          >
            <ResetPasswordIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => handleDelete(params.row)}
            sx={{ color: theme.palette.error.main }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Stack>
      ),
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Stack spacing={3}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h4" fontWeight={600}>
              Alumni User Management
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Manage student alumni accounts for website and LINE LIFF access
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreate}
          >
            Create Alumni User
          </Button>
        </Box>

        {error && (
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Card
          sx={{
            border: `1px solid ${theme.palette.divider}`,
            boxShadow: theme.shadows[1],
          }}
        >
          <Box sx={{ p: 2, borderBottom: `1px solid ${theme.palette.divider}` }}>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Status Filter</InputLabel>
              <Select
                value={statusFilter}
                label="Status Filter"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="all">All Users</MenuItem>
                <MenuItem value="active">Active Only</MenuItem>
                <MenuItem value="inactive">Inactive Only</MenuItem>
              </Select>
            </FormControl>
          </Box>
          
          <DataGrid
            rows={filteredUsers}
            columns={columns}
            loading={loading}
            autoHeight
            pageSizeOptions={[10, 25, 50, 100]}
            initialState={{
              pagination: { paginationModel: { pageSize: 25 } },
            }}
            slots={{ toolbar: GridToolbar }}
            slotProps={{
              toolbar: {
                showQuickFilter: true,
                quickFilterProps: { debounceMs: 500 },
              },
            }}
            sx={{
              border: 'none',
              '& .MuiDataGrid-cell:focus': {
                outline: 'none',
              },
              '& .MuiDataGrid-row:hover': {
                bgcolor: alpha(theme.palette.primary.main, 0.04),
              },
            }}
          />
        </Card>
      </Stack>

      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {selectedUser ? 'Edit Alumni User' : 'Create Alumni User'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 2 }}>
            <TextField
              label="Email"
              type="email"
              fullWidth
              required
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              disabled={!!selectedUser}
            />
            <TextField
              label="First Name"
              fullWidth
              required
              value={formData.first_name}
              onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
            />
            <TextField
              label="Last Name"
              fullWidth
              required
              value={formData.last_name}
              onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
            />
            <TextField
              label="Phone"
              fullWidth
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
            />
            {selectedUser && (
              <>
                <TextField
                  label="Student ID"
                  fullWidth
                  value={(selectedUser as AlumniUser).student_id || 'Not linked to Alumni record'}
                  InputProps={{
                    readOnly: true,
                    style: { fontFamily: 'monospace' }
                  }}
                  helperText="From linked Alumni profile"
                />
                <TextField
                  label="Citizen ID"
                  fullWidth
                  value={(selectedUser as AlumniUser).citizen_id || 'Not linked to Alumni record'}
                  InputProps={{
                    readOnly: true,
                    style: { fontFamily: 'monospace' }
                  }}
                  helperText="From linked Alumni profile"
                />
              </>
            )}
            <TextField
              label="LINE User ID"
              fullWidth
              value={formData.line_user_id}
              onChange={(e) => setFormData({ ...formData, line_user_id: e.target.value })}
              helperText="Optional: Link this account to a LINE user"
            />
            <TextField
              label={selectedUser ? 'New Password (leave blank to keep current)' : 'Password'}
              type="password"
              fullWidth
              required={!selectedUser}
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
            />
            <Alert severity="info" sx={{ mt: 1 }}>
              This user will have access to the alumni website and LINE LIFF features.
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" color="primary">
            {selectedUser ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete alumni user <strong>{selectedUser?.email}</strong>?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleConfirmDelete} variant="contained" color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default AlumniUserManagement;
